import { useSyncExternalStore, type ReactNode } from 'react';
import {
	getTree,
	getVariants,
	type VehicleTree,
	type VehicleVariant,
	type VehicleVariants,
} from '../api/vehicles';
import { notifyVehicleSelected } from '../utils/orderFunnel';

export interface DropdownOption {
	value: string;
	label: string;
}

// ---------------------------------------------------------------------------
// External store – module-level singleton, survives HMR & navigation
// ---------------------------------------------------------------------------

// sessionStorage on purpose: the selection survives navigation and reloads
// within the visit, but a new browser session starts with no car selected.
const STORAGE_KEY = 'akku24_vehicle_selection';

interface VehicleState {
	selectedFuelType: string;
	selectedBrand: string;
	selectedModel: string;
	selectedType: string;
	selectedVehicleId: number | null;
	fuelTypeOptions: DropdownOption[];
	brandOptions: DropdownOption[];
	modelOptions: DropdownOption[];
	typeOptions: DropdownOption[];
	loading: boolean;
}

function loadFromStorage(): { fuelType: string; brand: string; model: string; type: string } {
	try {
		const stored = sessionStorage.getItem(STORAGE_KEY);
		if (stored) return JSON.parse(stored);
	} catch { /* ignore */ }
	return { fuelType: '', brand: '', model: '', type: '' };
}

const stored = loadFromStorage();

let state: VehicleState = {
	selectedFuelType: stored.fuelType,
	selectedBrand: stored.brand,
	selectedModel: stored.model,
	selectedType: stored.type,
	selectedVehicleId: stored.type ? Number(stored.type) : null,
	fuelTypeOptions: [],
	brandOptions: [],
	modelOptions: [],
	typeOptions: [],
	loading: false,
};

const listeners = new Set<() => void>();

function emit() {
	listeners.forEach((l) => l());
}

function persist() {
	sessionStorage.setItem(
		STORAGE_KEY,
		JSON.stringify({
			fuelType: state.selectedFuelType,
			brand: state.selectedBrand,
			model: state.selectedModel,
			type: state.selectedType,
		}),
	);
}

function setState(patch: Partial<VehicleState>) {
	state = { ...state, ...patch };
	emit();
}

// ---------------------------------------------------------------------------
// The catalogue: one request brings the whole fuel type → brand → model tree
// (GET /vehicle/tree, ~50 KB gzipped, kept by the browser for 5 minutes); the
// variants of a brand come in one more request the moment the brand is picked,
// so by the time a model is chosen its list is already here. Once the tree is
// in, no dropdown step waits for the network.
// ---------------------------------------------------------------------------

let treePromise: Promise<VehicleTree> | null = null;
let treeLoaded: VehicleTree | null = null;
const variantPromises = new Map<string, Promise<VehicleVariants>>();
const variantsLoaded = new Map<string, VehicleVariants>();

function loadTree(): Promise<VehicleTree> {
	if (!treePromise) {
		treePromise = getTree()
			.then((res) => {
				const tree = (res.data?.data || {}) as VehicleTree;
				treeLoaded = tree;
				return tree;
			})
			.catch((e) => {
				treePromise = null; // the next step asks again
				throw e;
			});
	}
	return treePromise;
}

function loadVariants(fuelType: string, brand: string): Promise<VehicleVariants> {
	const key = `${fuelType}|${brand}`;
	let p = variantPromises.get(key);
	if (!p) {
		p = getVariants(fuelType, brand)
			.then((res) => {
				const bundle = (res.data?.data || {}) as VehicleVariants;
				variantsLoaded.set(key, bundle);
				return bundle;
			})
			.catch((e) => {
				variantPromises.delete(key);
				throw e;
			});
		variantPromises.set(key, p);
	}
	return p;
}

function brandOptionsOf(tree: VehicleTree, fuelType: string): DropdownOption[] {
	return (tree.brands?.[fuelType] || []).map((b) => ({ value: b, label: b }));
}

function modelOptionsOf(tree: VehicleTree, fuelType: string, brand: string): DropdownOption[] {
	return (tree.model_types?.[fuelType]?.[brand] || []).map((m) => ({ value: String(m.id), label: m.label }));
}

// The variants of one model: under the id the tree carries, or — should the tree
// and the bundle come from different moments — under its label.
function typeOptionsOf(bundle: VehicleVariants, modelId: string, modelLabel: string): DropdownOption[] {
	const groups = bundle.groups || {};
	let items: VehicleVariant[] = groups[modelId]?.items || [];
	if (!items.length) {
		for (const id of Object.keys(groups)) {
			if (groups[id].label === modelLabel) {
				items = groups[id].items || [];
				break;
			}
		}
	}
	return items.map((t) => {
		let label = t.label || t.model || '';
		if (t.built_from || t.built_to) label += ` (${t.built_from || '?'} - ${t.built_to || ''})`;
		if (t.power_kw) label += ` ${t.power_kw} kW`;
		return { value: String(t.id), label };
	});
}

// --- Loaders (a change made while an answer was on its way must not fill a list twice) ---

const tickets = { brand: 0, model: 0, type: 0 };

function loadBrands(fuelType: string) {
	const ticket = ++tickets.brand;
	if (!fuelType) {
		setState({ brandOptions: [], modelOptions: [], typeOptions: [] });
		return;
	}
	if (treeLoaded) {
		setState({ brandOptions: brandOptionsOf(treeLoaded, fuelType), modelOptions: [], typeOptions: [] });
		return;
	}
	setState({ loading: true, brandOptions: [], modelOptions: [], typeOptions: [] });
	loadTree()
		.then((tree) => {
			if (ticket !== tickets.brand) return;
			setState({ brandOptions: brandOptionsOf(tree, fuelType), loading: false });
		})
		.catch(() => {
			if (ticket === tickets.brand) setState({ brandOptions: [], loading: false });
		});
}

function loadModels(fuelType: string, brand: string) {
	const ticket = ++tickets.model;
	if (!brand || !fuelType) {
		setState({ modelOptions: [], typeOptions: [] });
		return;
	}
	// this brand's variants, so the model's list is here before it is asked for
	loadVariants(fuelType, brand).catch(() => { /* asked again when needed */ });
	if (treeLoaded) {
		setState({ modelOptions: modelOptionsOf(treeLoaded, fuelType, brand), typeOptions: [] });
		return;
	}
	setState({ loading: true, modelOptions: [], typeOptions: [] });
	loadTree()
		.then((tree) => {
			if (ticket !== tickets.model) return;
			setState({ modelOptions: modelOptionsOf(tree, fuelType, brand), loading: false });
		})
		.catch(() => {
			if (ticket === tickets.model) setState({ modelOptions: [], loading: false });
		});
}

function loadTypes(modelId: string) {
	const ticket = ++tickets.type;
	const fuelType = state.selectedFuelType;
	const brand = state.selectedBrand;
	if (!modelId || !fuelType || !brand) {
		setState({ typeOptions: [] });
		return;
	}
	const modelLabel = () => state.modelOptions.find((o) => o.value === modelId)?.label || '';
	const loaded = variantsLoaded.get(`${fuelType}|${brand}`);
	if (loaded) {
		setState({ typeOptions: typeOptionsOf(loaded, modelId, modelLabel()) });
		return;
	}
	setState({ loading: true, typeOptions: [] });
	loadVariants(fuelType, brand)
		.then((bundle) => {
			if (ticket !== tickets.type) return;
			setState({ typeOptions: typeOptionsOf(bundle, modelId, modelLabel()), loading: false });
		})
		.catch(() => {
			if (ticket === tickets.type) setState({ typeOptions: [], loading: false });
		});
}

// --- Public setters (cascade + load) ---

function setSelectedFuelType(value: string) {
	setState({
		selectedFuelType: value,
		selectedBrand: '',
		selectedModel: '',
		selectedType: '',
		selectedVehicleId: null,
	});
	persist();
	loadBrands(value);
}

function setSelectedBrand(value: string) {
	setState({
		selectedBrand: value,
		selectedModel: '',
		selectedType: '',
		selectedVehicleId: null,
	});
	persist();
	loadModels(state.selectedFuelType, value);
}

function setSelectedModel(value: string) {
	setState({
		selectedModel: value,
		selectedType: '',
		selectedVehicleId: null,
	});
	persist();
	loadTypes(value);
}

function setSelectedType(value: string) {
	setState({
		selectedType: value,
		selectedVehicleId: value ? Number(value) : null,
	});
	persist();
	// The car is now fully identified — the classic sites' Order_Start moment.
	// Also stamps vehicle_id on the durable session row (order-matching car veto).
	if (value) notifyVehicleSelected(Number(value));
}

/**
 * Reset the vehicle selection (dropdowns + cascading options + persisted copy).
 * Triggered by the Törlés button in the vehicle selector.
 */
export function clearVehicleSelection() {
	// an answer still on its way must not fill the lists after the reset
	tickets.brand++;
	tickets.model++;
	tickets.type++;
	// Skip if already empty – avoids needless re-renders on every navigation.
	if (
		!state.selectedFuelType &&
		!state.selectedBrand &&
		!state.selectedModel &&
		!state.selectedType &&
		state.selectedVehicleId === null &&
		state.brandOptions.length === 0 &&
		state.modelOptions.length === 0 &&
		state.typeOptions.length === 0
	) {
		return;
	}
	setState({
		selectedFuelType: '',
		selectedBrand: '',
		selectedModel: '',
		selectedType: '',
		selectedVehicleId: null,
		brandOptions: [],
		modelOptions: [],
		typeOptions: [],
		loading: false,
	});
	try {
		sessionStorage.removeItem(STORAGE_KEY);
	} catch { /* ignore */ }
}

// --- Initial load ---

let fuelTypesLoaded = false;

function ensureFuelTypesLoaded() {
	if (fuelTypesLoaded) return;
	fuelTypesLoaded = true;
	setState({ loading: true });
	loadTree()
		.then((tree) => {
			setState({
				fuelTypeOptions: Object.entries(tree.combined || {}).map(([key, label]) => ({ value: key, label })),
				loading: false,
			});
		})
		.catch(() => setState({ fuelTypeOptions: [], loading: false }));

	// If we restored selections from sessionStorage, load their cascading options:
	// the tree and the brand's variants, side by side
	if (stored.fuelType) loadBrands(stored.fuelType);
	if (stored.fuelType && stored.brand) loadModels(stored.fuelType, stored.brand);
	if (stored.model) loadTypes(stored.model);
}

// ---------------------------------------------------------------------------
// React hook – every component subscribes independently to the store
// ---------------------------------------------------------------------------

function subscribe(listener: () => void) {
	listeners.add(listener);
	ensureFuelTypesLoaded();
	return () => {
		listeners.delete(listener);
	};
}

function getSnapshot(): VehicleState {
	return state;
}

export interface UseVehicleResult {
	fuelTypeOptions: DropdownOption[];
	brandOptions: DropdownOption[];
	modelOptions: DropdownOption[];
	typeOptions: DropdownOption[];
	selectedFuelType: string;
	selectedBrand: string;
	selectedModel: string;
	selectedType: string;
	setSelectedFuelType: (value: string) => void;
	setSelectedBrand: (value: string) => void;
	setSelectedModel: (value: string) => void;
	setSelectedType: (value: string) => void;
	selectedVehicleId: number | null;
	loading: boolean;
}

export function useVehicle(): UseVehicleResult {
	const snap = useSyncExternalStore(subscribe, getSnapshot);
	return {
		...snap,
		setSelectedFuelType,
		setSelectedBrand,
		setSelectedModel,
		setSelectedType,
	};
}

// ---------------------------------------------------------------------------
// VehicleProvider – now a thin passthrough (kept for backward compat)
// ---------------------------------------------------------------------------

export function VehicleProvider({ children }: { children: ReactNode }) {
	return <>{children}</>;
}
